import { useParams } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BarChart3, 
  Megaphone, 
  Building2, 
  Users, 
  UsersRound,
  CreditCard,
  Settings,
  HelpCircle,
} from 'lucide-react';
import { useOrganization } from '../../../domains/organizations/hooks/useOrganization';
import { AppLayout } from '../../../shared/components/layout/AppLayout';
import type { NavGroup } from '../../../shared/components/layout/AppLayout';
import { OrgSwitcher } from '../../../shared/components/layout/OrgSwitcher';
import { SidebarCallout } from '../../../shared/components/layout/SidebarCallout';

function OrgHeader() {
  return (
    <div className="flex items-center h-16 px-2">
      <OrgSwitcher />
    </div>
  );
}

function PartnerPortalNotice() {
  return (
    <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
      <p>
        <span className="font-semibold">We've moved.</span>{' '}
        <span className="text-blue-800">
          Your creator inbox now lives in the new partner portal — rebuilt for a faster, cleaner way
          to review applicants and manage your pipeline. Your account and existing applications have
          already been migrated, so you can pick up right where you left off.
        </span>
      </p>
      <a
        href="https://partner.videreo.com"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1.5 inline-block font-medium underline underline-offset-2 hover:text-blue-950"
      >
        Go to partner.videreo.com →
      </a>
    </div>
  );
}

function OrgLayoutContent({ children }: { children?: React.ReactNode }) {
  const { organization, isAgency } = useOrganization();

  if (!organization) {
    return null;
  }

  const baseUrl = `/org/${organization.slug}`;

  const navGroups: NavGroup[] = [
    {
      items: [
        { title: 'Dashboard', url: `${baseUrl}/dashboard`, icon: LayoutDashboard },
      ],
    },
    {
      title: 'Operations',
      items: [
        { title: 'Campaigns', url: `${baseUrl}/campaigns`, icon: Megaphone },
        { title: 'Creators', url: `${baseUrl}/creators`, icon: Users },
        { title: 'Campaign Reports', url: `${baseUrl}/reports`, icon: BarChart3 },
      ],
    },
    {
      title: 'Management',
      items: [
        { title: 'Clients', url: `${baseUrl}/clients`, icon: Building2, show: isAgency },
      ],
    },
    {
      title: 'Administration',
      items: [
        { title: 'Team', url: `${baseUrl}/team`, icon: UsersRound },
        { title: 'Billing', url: `${baseUrl}/billing`, icon: CreditCard },
        { title: 'Settings', url: `${baseUrl}/settings`, icon: Settings },
        { title: 'Help', url: `${baseUrl}/help`, icon: HelpCircle },
      ],
    },
  ];

  return (
      <AppLayout
        headerSlot={<OrgHeader />}
        navGroups={navGroups}
        mode="org"
        preFooterSlot={<SidebarCallout showWhatsApp={false} />}
        siteHeaderTitle={organization.name}
      >
        <PartnerPortalNotice />
        {children}
      </AppLayout>
  );
}

export function OrgLayout({ children }: { children?: React.ReactNode }) {
  const { organization } = useOrganization();
  const { slug } = useParams<{ slug: string }>();

  if (!organization || organization.slug !== slug) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return <OrgLayoutContent>{children}</OrgLayoutContent>;
}

export default OrgLayout;
